import {
    Box,
    IconButton,
    Menu,
    MenuItem,
    TableCell,
    TableRow,
    Theme,
    Typography
} from "@mui/material";
import {
    checkImageExists,
    formatCurrency
} from "../../../../utils/utilityFunctions";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import { useState } from "react";
import {
    ActionIcon,
    BlackDownArrowIcon
} from "../../../../assets/SvgIcons";
import { useHistory } from "react-router";
import dayjs from "dayjs";

const amcIds = ['BDOTR', 'BPIAMC']

const statusColorMap: { [key: string]: string } = {
    active: "success.main",
    inactive: "error.main",
};


function SchemeImageComponent({
    amcId,
    schemeName
}: {
    amcId: string;
    schemeName: string
}) {
    const checkAmcId = amcIds.includes(amcId ?? '') ? amcId?.toLowerCase() : amcId;

    return (
        <Box
            sx={{
                borderRadius: "50%",
                overflow: "hidden",
                width: "35px",
                height: "35px",
                flexShrink: 0,
            }}
        >
            <Box
                component='img'
                src={checkImageExists(`${checkAmcId}`) ? `/images/amcIcons/${checkAmcId}.svg` : `/images/amcIcons/default.svg`}
                alt={schemeName}
                sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    flexShrink: 0,
                }}
            />
        </Box>
    )
}

function SchemeCell({
    amcId,
    fromScheme,
    toScheme,
    type,
    folioNum
}: {
    amcId: string;
    fromScheme: string;
    toScheme: string;
    type: string;
    folioNum: string;
}) {
    if (type === "STP") {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    rowGap: "15px",
                }}>
                <Box
                    sx={{
                        display: "flex",
                        columnGap: "12px",
                        alignItems: "center"
                    }}
                >
                    <SchemeImageComponent amcId={amcId} schemeName={fromScheme} />
                    <Typography
                        variant="body_500"
                        sx={(theme) => ({
                            lineHeight: "20px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.primary,
                            '&:hover': {
                                color: theme.palette.primary[500],
                            },
                        })}
                    >
                        {folioNum ? `${fromScheme} (${folioNum})` : `${fromScheme}`}
                    </Typography>
                </Box>
                <BlackDownArrowIcon />
                <Box
                    sx={{
                        display: "flex",
                        columnGap: "12px",
                        alignItems: "center"
                    }}
                >
                    <SchemeImageComponent amcId={amcId} schemeName={toScheme} />
                    <Typography
                        variant="body_500"
                        sx={(theme) => ({
                            lineHeight: "20px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.primary,
                            '&:hover': {
                                color: theme.palette.primary[500],
                            },
                        })}
                    >
                        {folioNum ? `${toScheme} (${folioNum})` : `${toScheme}`}
                    </Typography>
                </Box>
            </Box>
        )
    }

    return (
        <Box
            sx={{
                display: "flex",
                columnGap: "12px",
                alignItems: "center"
            }}
        >
            <SchemeImageComponent amcId={amcId} schemeName={fromScheme} />
            <Typography
                variant="body_500"
                sx={(theme) => ({
                    lineHeight: "20px",
                    letterSpacing: "0.02px",
                    color: theme.palette.text.primary,
                    '&:hover': {
                        color: theme.palette.primary[500],
                    },
                })}
            >
                {folioNum ? `${fromScheme} (${folioNum})` : `${fromScheme}`}
            </Typography>
        </Box>
    )
}

export default function SystematicPlansTable({
    data,
    type,
    handlePause,
    handleCancel,
    handleSwitchMandate
}: {
    data: { [key: string]: any }[];
    type: string;
    handlePause: (id: string, type: string) => void
    handleCancel: (id: string, type: string) => void
    handleSwitchMandate: (amount: number | string, id: string) => void
}): JSX.Element {
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [activeRowId, setActiveRowId] = useState<string | null>(null);
    const [activeSip, setActiveSip] = useState<boolean>(false);
    const [amount, setAmount] = useState<number | string>('');

    const history = useHistory();
    const { pathname } = history.location;
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const { clientInterfaceCode, viewAsFamilyId = "" } = useSelector((state: RootStateType) => state.id)

    const isFamilyView = pathname.includes('investor-family') && clientInterfaceCode !== viewAsFamilyId;
    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, id: string, amount: number | string, status: string) => {
        const checkActiveStatus = (status ?? "")?.toLowerCase() === "active";
        setAmount(amount);
        setMenuAnchor(event.currentTarget);
        setActiveRowId(id);
        setActiveSip(checkActiveStatus);
    };
 
    const handleCloseMenu = () => {
        setMenuAnchor(null);
        setActiveRowId(null);
        setActiveSip(false);
    };

    return (
        <>
            {data.map((obj: { [key: string]: any }, index: number) => {
                console.log(`amount-${index}`, obj?.installmentAmount);
                return (
                    <TableRow
                        key={`${obj?.scheme}-${index}`}
                        sx={(theme: Theme) => ({
                            '& td': {
                                paddingTop: "16px",
                                paddingBottom: "16px",
                                borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                ':first-of-type': {
                                    paddingLeft: "0px",
                                }
                            },
                        })}
                    >
                        <TableCell align="left">
                            <SchemeCell
                                amcId={obj?.amcId ?? ""}
                                fromScheme={obj?.schemeName ?? ""}
                                toScheme={obj?.toSchemeName ?? ""}
                                type={type}
                                folioNum={obj?.folioNo ?? ""}
                            />

                        </TableCell>
                        {type !== "STP" && type !== "SWP" && (
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    {obj?.mandateId ? obj?.mandateId : "-"}
                                </Typography>
                            </TableCell>
                        )}

                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary
                                })}
                            >
                               {obj?.registrationNo ? obj?.registrationNo : "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary
                                })}
                            >
                                {formatCurrency({ value: Number(obj?.installmentAmount ?? 0), locale, currencyCode })}
                            </Typography>
                        </TableCell>
                         <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary,
                                })}
                            >
                                {(obj?.investedValue ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(obj?.investedValue ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: obj?.investedValue ?? 0, locale, currencyCode })}`}
                            </Typography>
                        </TableCell>
                        {/* <TableCell
                    align="center"
                    sx={{ paddingLeft: "0px" }}
                >
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            lineHeight: "20px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.primary
                        })}
                    >
                        {obj.instalmentNo}
                    </Typography>
                </TableCell> */}
                <TableCell align="center">
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    rowGap: "4px",
                                }}
                            >
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    {obj?.registrationDate ? dayjs(obj?.registrationDate).format('DD/MM/YYYY') : "-"}
                                </Typography>
                                <Box
                                    sx={(theme: Theme) => ({
                                        width: "100%",
                                        borderTop: `0.5px solid ${theme.palette.text.secondary}B2`,
                                    })}
                                />
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    {obj?.startDate ? dayjs(obj?.startDate).format('DD/MM/YYYY') : "-"}
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell align="center">
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    rowGap: "4px",
                                }}
                            >
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    {obj?.nextSipDate ? dayjs(obj?.nextSipDate).format('DD/MM/YYYY') : "-"}
                                </Typography>
                                <Box
                                    sx={(theme: Theme) => ({
                                        width: "100%",
                                        borderTop: `0.5px solid ${theme.palette.text.secondary}B2`,
                                    })}
                                />
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    {obj?.endDate ? dayjs(obj?.endDate).format('DD/MM/YYYY') : "-"}
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                color={statusColorMap[`${obj?.activeInactive ?? ""}`.toLowerCase()] ?? 'text.primary'}
                                 sx={{
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px"
                              }}
                            >
                              {obj?.activeInactive ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary
                                })}
                            >
                                {obj?.transactionMode ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <IconButton
                                id="action-button"
                                aria-label="action-button"
                                aria-controls={menuAnchor ? 'action-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={menuAnchor ? 'true' : undefined}
                                onClick={(event) => handleOpenMenu(event, obj?.registrationNo ?? "", obj?.installmentAmount ?? 0, obj?.activeInactive ?? "")}
                                disabled={isFamilyView || (obj?.transactionMode ?? "")?.toLowerCase() === "offline" || (obj?.activeInactive ?? "")?.toLowerCase() === "inactive"}
                                sx={{
                                    "&.Mui-disabled": {
                                        opacity: 0.5,
                                        cursor: "not-allowed",
                                        pointerEvents: "auto",
                                    }
                                }}
                            >
                                <ActionIcon />
                            </IconButton>
                            <Menu
                                id="action-menu"
                                aria-labelledby="action-button"
                                anchorEl={menuAnchor}
                                open={Boolean(menuAnchor)}
                                onClose={handleCloseMenu}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center'
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center'
                                }}
                                sx={{
                                    boxShadow: 'none',
                                    '& .MuiPaper-root': {
                                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                        maxWidth: '180px',
                                        minWidth: '150px',
                                    }
                                }}
                            >
                                {type === 'SIP' && activeSip && [
                                    <MenuItem
                                        key="pause-sip"
                                        onClick={() => { handlePause(activeRowId ?? "", 'SIP'); handleCloseMenu(); }}
                                        sx={(theme: Theme) => ({
                                            margin: "2px 5px 5px 5px",
                                            padding: "10px",
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary[100],
                                                borderRadius: "5px"
                                            }
                                        })}
                                    >
                                        <Typography
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "16px",
                                                color: theme.palette.text.secondary,
                                                '&:hover': {
                                                    color: theme.palette.primary[500]
                                                }
                                            })}
                                        >
                                            Pause SIP
                                        </Typography>
                                    </MenuItem>,
                                    <MenuItem
                                        key="cancel-sip"
                                        onClick={() => { handleCancel(activeRowId ?? "", 'SIP'); handleCloseMenu(); }}
                                        sx={(theme: Theme) => ({
                                            margin: "5px 5px 2px 5px",
                                            padding: "10px",
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary[100],
                                                borderRadius: "5px"
                                            }
                                        })}
                                    >
                                        <Typography
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "16px",
                                                color: theme.palette.text.secondary,
                                                '&:hover': {
                                                    color: theme.palette.primary[500]
                                                }
                                            })}
                                        >
                                            Cancel SIP
                                        </Typography>
                                    </MenuItem>,
                                    // <MenuItem
                                    //     key="switch-mandate"
                                    //     onClick={() => { handleSwitchMandate(amount, activeRowId ?? ""); handleCloseMenu(); }}
                                    //     sx={(theme: Theme) => ({
                                    //         margin: "5px 5px 2px 5px",
                                    //         padding: "10px",
                                    //         '&:hover': {
                                    //             backgroundColor: theme.palette.primary[50],
                                    //             borderRadius: "5px"
                                    //         }
                                    //     })}
                                    // >
                                    //     <Typography
                                    //         variant="body_500"
                                    //         sx={(theme: Theme) => ({
                                    //             lineHeight: "16px",
                                    //             color: theme.palette.text.secondary,
                                    //             '&:hover': {
                                    //                 color: theme.palette.primary[500]
                                    //             }
                                    //         })}
                                    //     >
                                    //         Switch Mandate
                                    //     </Typography>
                                    // </MenuItem>
                                ]}
                                {type === 'STP' && activeRowId && (
                                    <MenuItem
                                        key="cancel-stp"
                                        onClick={() => { handleCancel(activeRowId, 'STP'); handleCloseMenu(); }}
                                        sx={(theme: Theme) => ({
                                            margin: "0px 5px 0px 5px",
                                            padding: "10px",
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary[100],
                                                borderRadius: "5px"
                                            }
                                        })}
                                    >
                                        <Typography
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "16px",
                                                color: theme.palette.text.secondary,
                                                '&:hover': {
                                                    color: theme.palette.primary[500]
                                                }
                                            })}
                                        >
                                            Cancel STP
                                        </Typography>
                                    </MenuItem>
                                )}
                                {type === 'SWP' && activeRowId && (
                                    <MenuItem
                                        key="cancel-swp"
                                        onClick={() => { handleCancel(activeRowId, 'SWP'); handleCloseMenu(); }}
                                        sx={(theme: Theme) => ({
                                            margin: "0px 5px 0px 5px",
                                            padding: "10px",
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary[50],
                                                borderRadius: "5px"
                                            }
                                        })}
                                    >
                                        <Typography
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "16px",
                                                color: theme.palette.text.secondary,
                                                '&:hover': {
                                                    color: theme.palette.primary[500]
                                                }
                                            })}
                                        >
                                            Cancel SWP
                                        </Typography>
                                    </MenuItem>
                                )}
                            </Menu>
                        </TableCell>
                    </TableRow>
                )
            })}
        </>
    )
}

// Mobile Responsive Component for Systematic Plans
export function MobileResponsiveSystematicPlans({
    data,
    type,
    tableHeaderData,
    handlePause,
    handleCancel
}: {
    data: { [key: string]: any }[];
    type: string;
    tableHeaderData: { id: string; label: string; align: string; isSortable: boolean }[];
    handlePause: (id: string, type: string) => void;
    handleCancel: (id: string, type: string) => void;
}): JSX.Element {
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [activeRowId, setActiveRowId] = useState<string | null>(null);
    const [activeSip, setActiveSip] = useState<boolean>(false);
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const [amount, setAmount] = useState<number | string>('');
    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, id: string, amount: number | string, status: string) => {
        const checkActiveStatus = (status ?? "")?.toLowerCase() === "active";
        setAmount(amount);
        setMenuAnchor(event.currentTarget);
        setActiveRowId(id);
        setActiveSip(checkActiveStatus);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
        setActiveRowId(null);
        setActiveSip(false);
    };

    const labelFor = (id: string) =>
        tableHeaderData.find(h => h.id === id)?.label || id;

    return (
        <>
            {data.map((obj: { [key: string]: any }, index: number) => (
                <Box
                    key={`${obj?.scheme}-${index}`}
                    sx={(theme: Theme) => ({
                        border: `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: "12px",
                        padding: "16px",
                        marginBottom: "16px",
                        backgroundColor: theme.palette.common.white,
                        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)"
                    })}
                >
                    {/* Scheme Header with Image and Name */}
                    <Box
                        sx={(theme: Theme) => ({
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "16px",
                            paddingBottom: "12px",
                            borderBottom: `1px solid ${theme.palette.grey[100]}`
                        })}
                    >
                        <SchemeImageComponent
                            amcId={obj?.amcId ?? ""}
                            schemeName={obj?.schemeName ?? ""}
                        />
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary,
                                    fontWeight: 600,
                                    fontSize: "16px"
                                })}
                            >
                                {obj?.schemeName ?? ""}
                            </Typography>
                            {obj?.folioNo && (
                                <Typography
                                    variant="caption"
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.secondary,
                                        fontSize: "12px"
                                    })}
                                >
                                    Folio: {obj?.folioNo}
                                </Typography>
                            )}
                        </Box>
                        {/* Action Menu */}
                        <IconButton
                            onClick={(event) => handleOpenMenu(event, obj?.registrationNo ?? "", obj?.installmentAmount ?? 0, obj?.activeInactive ?? "")}
                            disabled={(obj?.transactionMode ?? "")?.toLowerCase() === "offline" || (obj?.activeInactive ?? "")?.toLowerCase() === "inactive"}
                            sx={(theme: Theme) => ({
                                padding: "4px",
                                color: theme.palette.text.secondary,
                                "&.Mui-disabled": {
                                    cursor: "not-allowed",
                                    pointerEvents: "auto",
                                    opacity: 0.5,
                                }
                            })}
                        >
                            <ActionIcon />
                        </IconButton>
                    </Box>

                    {/* Data Grid - 2x2 Layout using tableHeaderData */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "16px"
                        }}
                    >
                        {/* Left Top: Installment Amount */}
                        <Box>
                            <Typography
                                variant="caption"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.secondary,
                                    fontSize: "12px",
                                    lineHeight: "16px",
                                    marginBottom: "4px",
                                    display: "block",
                                    textAlign: "left",
                                })}
                            >
                                {labelFor("installmentAmount")}
                            </Typography>
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary,
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    textAlign: "left",
                                })}
                            >
                                {formatCurrency({ value: Number(obj?.installmentAmount ?? 0), locale, currencyCode })}
                            </Typography>
                        </Box>

                        {/* Right Top: End Date */}
                        <Box>
                            <Typography
                                variant="caption"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.secondary,
                                    fontSize: "12px",
                                    lineHeight: "16px",
                                    marginBottom: "4px",
                                    display: "block",
                                    textAlign: "right",
                                })}
                            >
                                {labelFor("endDate")}
                            </Typography>
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary,
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    textAlign: "right",
                                })}
                            >
                                {obj?.endDate ?? "-"}
                            </Typography>
                        </Box>

                        {/* Left Bottom: Status */}
                        <Box>
                            <Typography
                                variant="caption"
                                sx={(theme: Theme) => ({
                                     color: statusColorMap[`${obj?.activeInactive ?? ""}`.toLowerCase()] ?? 'text.primary',
                                    fontSize: "12px",
                                    lineHeight: "16px",
                                    marginBottom: "4px",
                                    display: "block",
                                    textAlign: "left",
                                })}
                            >
                                {labelFor("activeInactive")}
                            </Typography>
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary,
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    textAlign: "left",
                                })}
                            >
                                {obj?.activeInactive ?? "-"}
                            </Typography>
                        </Box>

                        {/* Right Bottom: keep empty or add any future field */}
                        <Box />
                    </Box>

                    {/* Action Menu */}
                    <Menu
                        id={`action-menu-${index}`}
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor) && activeRowId === obj?.registrationNo}
                        onClose={handleCloseMenu}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center'
                        }}
                        sx={{
                            boxShadow: 'none',
                            '& .MuiPaper-root': {
                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                maxWidth: '180px',
                                minWidth: '150px',
                            }
                        }}
                    >
                        {type === 'SIP' && activeSip && [
                            <MenuItem
                                key="pause-sip"
                                onClick={() => { handlePause(activeRowId ?? "", 'SIP'); handleCloseMenu(); }}
                                sx={(theme: Theme) => ({
                                    margin: "2px 5px 5px 5px",
                                    padding: "10px",
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary[100],
                                        borderRadius: "5px"
                                    }
                                })}
                            >
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "16px",
                                        color: theme.palette.text.secondary,
                                        '&:hover': {
                                            color: theme.palette.primary[500]
                                        }
                                    })}
                                >
                                    Pause SIP
                                </Typography>
                            </MenuItem>,
                            <MenuItem
                                key="cancel-sip"
                                onClick={() => { handleCancel(activeRowId ?? "", 'SIP'); handleCloseMenu(); }}
                                sx={(theme: Theme) => ({
                                    margin: "5px 5px 2px 5px",
                                    padding: "10px",
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary[100],
                                        borderRadius: "5px"
                                    }
                                })}
                            >
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "16px",
                                        color: theme.palette.text.secondary,
                                        '&:hover': {
                                            color: theme.palette.primary[500]
                                        }
                                    })}
                                >
                                    Cancel SIP
                                </Typography>
                            </MenuItem>
                        ]}
                        {type === 'STP' && activeRowId && (
                            <MenuItem
                                key="cancel-stp"
                                onClick={() => { handleCancel(activeRowId, 'STP'); handleCloseMenu(); }}
                                sx={(theme: Theme) => ({
                                    margin: "0px 5px 0px 5px",
                                    padding: "10px",
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary[100],
                                        borderRadius: "5px"
                                    }
                                })}
                            >
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "16px",
                                        color: theme.palette.text.secondary,
                                        '&:hover': {
                                            color: theme.palette.primary[500]
                                        }
                                    })}
                                >
                                    Cancel STP
                                </Typography>
                            </MenuItem>
                        )}
                        {type === 'SWP' && activeRowId && (
                            <MenuItem
                                key="cancel-swp"
                                onClick={() => { handleCancel(activeRowId, 'SWP'); handleCloseMenu(); }}
                                sx={(theme: Theme) => ({
                                    margin: "0px 5px 0px 5px",
                                    padding: "10px",
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary[100],
                                        borderRadius: "5px"
                                    }
                                })}
                            >
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "16px",
                                        color: theme.palette.text.secondary,
                                        '&:hover': {
                                            color: theme.palette.primary[500]
                                        }
                                    })}
                                >
                                    Cancel SWP
                                </Typography>
                            </MenuItem>
                        )}
                    </Menu>
                </Box>
            ))}
        </>
    );
}

